import { z } from 'zod';

/**
 * Wave 4 enums: the variable pay engine and sealed compensation (SPEC §7.9, PLAN §3.3 sec. 12,
 * D-066, D-097 to D-105, D-880 ff.). The CHECK constraints of the tables in
 * packages/db/src/schema/pay.ts are built from these lists. The period close machine itself (which
 * status may follow which) lives in `@orbit/domain/pay` `periodMachine.ts`; the database function
 * `pay_period_transition_allowed` is generated from it (migration 0023).
 *
 * Nothing here is an amount: sealed amounts are decimal strings of paise inside an AES-256-GCM
 * envelope, and pay events never carry amounts.
 */

// ---------------------------------------------------------------- files that wave 4 adds

/**
 * `files.purpose` values wave 4 adds to `FILE_PURPOSES` (enums.ts). The `files_purpose_check`
 * constraint is built from the union of both lists, so a purpose that is in only one of them still
 * passes. `plan_letter`, `payout_export` and `pay_evidence` files are encrypted in the application
 * before upload and served only through logged, streamed endpoints (D-066); `training_certificate`
 * is the deterministic certificate PDF (D-095).
 */
export const FILE_PURPOSES_WAVE4 = [
  'training_certificate',
  'plan_letter',
  'payout_export',
  'pay_evidence',
] as const;
export type FilePurposeWave4 = (typeof FILE_PURPOSES_WAVE4)[number];

// ---------------------------------------------------------------- sealed keys and access log

/** `sealed_keys.status`: a retiring data key still opens old rows until `pay:reseal` empties it. */
export const SEALED_KEY_STATUSES = ['active', 'retiring', 'retired'] as const;
export type SealedKeyStatus = (typeof SEALED_KEY_STATUSES)[number];
export const sealedKeyStatusSchema = z.enum(SEALED_KEY_STATUSES);

/** `compensation_access_log.resource`. */
export const COMPENSATION_ACCESS_RESOURCES = [
  'compensation_record',
  'pay_computation',
  'pay_adjustment',
  'payout_export',
  'simulation',
  // Plan amounts and letters are individual pay for a person-level plan (D-066): reading them
  // goes through the vault too (purpose `plan_version`), and so does a scorecard's indicative pay.
  'plan_version',
  'scorecard_indicative',
  // Wave 4B (D-951): dispute evidence and other sealed `pay_evidence` files, read through their
  // logged download endpoint, are logged under their own resource (migration 0025).
  'pay_evidence',
] as const;
export type CompensationAccessResource = (typeof COMPENSATION_ACCESS_RESOURCES)[number];
export const compensationAccessResourceSchema = z.enum(COMPENSATION_ACCESS_RESOURCES);

/** `compensation_access_log.action`. Denied attempts are logged too. */
export const COMPENSATION_ACCESS_ACTIONS = [
  'view',
  'create',
  'close',
  'decrypt_for_compute',
  'export',
  'denied',
] as const;
export type CompensationAccessAction = (typeof COMPENSATION_ACCESS_ACTIONS)[number];
export const compensationAccessActionSchema = z.enum(COMPENSATION_ACCESS_ACTIONS);

// ---------------------------------------------------------------- plans

/** `pay_plans.status`. */
export const PAY_PLAN_STATUSES = ['active', 'retired'] as const;
export type PayPlanStatus = (typeof PAY_PLAN_STATUSES)[number];
export const payPlanStatusSchema = z.enum(PAY_PLAN_STATUSES);

/** `pay_plan_versions.status`: a published version is immutable (trigger). */
export const PAY_PLAN_VERSION_STATUSES = ['draft', 'published'] as const;
export type PayPlanVersionStatus = (typeof PAY_PLAN_VERSION_STATUSES)[number];
export const payPlanVersionStatusSchema = z.enum(PAY_PLAN_VERSION_STATUSES);

/** `pay_plan_versions.mode`: shadow is indicative only and never exported (SPEC §7 principle 6). */
export const PAY_PLAN_MODES = ['shadow', 'live'] as const;
export type PayPlanMode = (typeof PAY_PLAN_MODES)[number];
export const payPlanModeSchema = z.enum(PAY_PLAN_MODES);

/** `pay_plan_versions.measurement_period_type` and `payout_period_type`. */
export const PAY_PERIOD_TYPES = ['month', 'quarter', 'half_year'] as const;
export type PayPeriodType = (typeof PAY_PERIOD_TYPES)[number];
export const payPeriodTypeSchema = z.enum(PAY_PERIOD_TYPES);

/** `pay_plan_versions.eligible_basis`: `none` is a pure slab (commission) plan. */
export const PAY_ELIGIBLE_BASES = ['fixed_amount', 'pct_of_fixed', 'none'] as const;
export type PayEligibleBasis = (typeof PAY_ELIGIBLE_BASES)[number];
export const payEligibleBasisSchema = z.enum(PAY_ELIGIBLE_BASES);

/** `pay_plan_versions.manager_payout_visibility` (NULL = the `pay.managerPayoutVisibility` setting). */
export const MANAGER_PAYOUT_VISIBILITIES = ['hidden', 'direct_reports', 'reporting_tree'] as const;
export type ManagerPayoutVisibilityValue = (typeof MANAGER_PAYOUT_VISIBILITIES)[number];
export const managerPayoutVisibilitySchema = z.enum(MANAGER_PAYOUT_VISIBILITIES);

/** `pay_plan_components.metric_type`. */
export const PAY_METRIC_TYPES = ['kpi', 'weighted_scorecard'] as const;
export type PayMetricType = (typeof PAY_METRIC_TYPES)[number];
export const payMetricTypeSchema = z.enum(PAY_METRIC_TYPES);

/** `pay_plan_components.curve_type`. */
export const PAY_CURVE_TYPES = ['linear', 'step', 'slab'] as const;
export type PayCurveType = (typeof PAY_CURVE_TYPES)[number];
export const payCurveTypeSchema = z.enum(PAY_CURVE_TYPES);

/** `pay_plan_gates.gate_type` (the `params` shapes are in the contracts). */
export const PAY_GATE_TYPES = [
  'report_submission_min_pct',
  'zero_critical_misses',
  'mandatory_training_complete',
  'min_tenure_days',
  'unlock_milestone',
  'kpi_min_achievement_pct',
] as const;
export type PayGateType = (typeof PAY_GATE_TYPES)[number];
export const payGateTypeSchema = z.enum(PAY_GATE_TYPES);

/** `pay_plan_gates.failure_mode`: `unlock_milestone` gates always `hold`. */
export const PAY_GATE_FAILURE_MODES = ['zero', 'hold'] as const;
export type PayGateFailureMode = (typeof PAY_GATE_FAILURE_MODES)[number];
export const payGateFailureModeSchema = z.enum(PAY_GATE_FAILURE_MODES);

/** `pay_plan_acknowledgements.method` and `pay_target_statements.method`. */
export const PAY_ACK_METHODS = ['in_app', 'offline_recorded'] as const;
export type PayAckMethod = (typeof PAY_ACK_METHODS)[number];
export const payAckMethodSchema = z.enum(PAY_ACK_METHODS);

// ---------------------------------------------------------------- periods and computations

/** `pay_periods.status` (the architecture's period-close machine, PLAN §3.6). */
export const PAY_PERIOD_STATUSES = [
  'open',
  'frozen',
  'verification_cleared',
  'computed',
  'shadow_published',
  'shadow_closed',
  'employee_review',
  'disputes_resolution',
  'pending_hr_approval',
  'pending_owner_approval',
  'locked',
  'exported',
] as const;
export type PayPeriodStatusValue = (typeof PAY_PERIOD_STATUSES)[number];
export const payPeriodStatusSchema = z.enum(PAY_PERIOD_STATUSES);

/** Statuses only a shadow period may take. */
export const PAY_SHADOW_ONLY_STATUSES = [
  'shadow_published',
  'shadow_closed',
] as const satisfies readonly PayPeriodStatusValue[];

/** Statuses a shadow period can never take: it can never reach payroll. */
export const PAY_LIVE_ONLY_STATUSES = [
  'employee_review',
  'disputes_resolution',
  'pending_hr_approval',
  'pending_owner_approval',
  'locked',
  'exported',
] as const satisfies readonly PayPeriodStatusValue[];

/** Statuses in which a period still takes adjustments (`open` to `disputes_resolution`). */
export const PAY_ADJUSTABLE_PERIOD_STATUSES = [
  'open',
  'frozen',
  'verification_cleared',
  'computed',
  'shadow_published',
  'employee_review',
  'disputes_resolution',
] as const satisfies readonly PayPeriodStatusValue[];

/** `pay_computations.status`: a recompute writes a new version and supersedes the old row. */
export const PAY_COMPUTATION_STATUSES = ['current', 'superseded'] as const;
export type PayComputationStatus = (typeof PAY_COMPUTATION_STATUSES)[number];
export const payComputationStatusSchema = z.enum(PAY_COMPUTATION_STATUSES);

/** `pay_computation_lines.gate_effect`. */
export const PAY_GATE_EFFECTS = ['none', 'zeroed', 'held'] as const;
export type PayGateEffect = (typeof PAY_GATE_EFFECTS)[number];
export const payGateEffectSchema = z.enum(PAY_GATE_EFFECTS);

/** `pay_computation_gates.result` and `pay_gate_overrides.overridden_result`. */
export const PAY_GATE_RESULTS = ['passed', 'failed', 'held'] as const;
export type PayGateResult = (typeof PAY_GATE_RESULTS)[number];
export const payGateResultSchema = z.enum(PAY_GATE_RESULTS);

// ---------------------------------------------------------------- disputes, adjustments, approvals

/** `pay_disputes.status`. */
export const PAY_DISPUTE_STATUSES = [
  'open',
  'under_review',
  'accepted',
  'rejected',
  'withdrawn',
] as const;
export type PayDisputeStatus = (typeof PAY_DISPUTE_STATUSES)[number];
export const payDisputeStatusSchema = z.enum(PAY_DISPUTE_STATUSES);

/** `pay_gate_overrides.status`. */
export const PAY_GATE_OVERRIDE_STATUSES = ['proposed', 'approved', 'rejected'] as const;
export type PayGateOverrideStatus = (typeof PAY_GATE_OVERRIDE_STATUSES)[number];
export const payGateOverrideStatusSchema = z.enum(PAY_GATE_OVERRIDE_STATUSES);

/** `pay_adjustments.adjustment_type`: holds and clawbacks are negative, releases positive. */
export const PAY_ADJUSTMENT_TYPES = [
  'hold',
  'release',
  'clawback',
  'correction',
  'forfeit',
  'recovery_carry_forward',
] as const;
export type PayAdjustmentType = (typeof PAY_ADJUSTMENT_TYPES)[number];
export const payAdjustmentTypeSchema = z.enum(PAY_ADJUSTMENT_TYPES);

/** `pay_adjustments.source`. */
export const PAY_ADJUSTMENT_SOURCES = [
  'manual',
  'unlock_gate',
  'clawback_rule',
  'dispute',
  'negative_balance',
] as const;
export type PayAdjustmentSource = (typeof PAY_ADJUSTMENT_SOURCES)[number];
export const payAdjustmentSourceSchema = z.enum(PAY_ADJUSTMENT_SOURCES);

/** `pay_adjustments.status`. */
export const PAY_ADJUSTMENT_STATUSES = [
  'proposed',
  'approved',
  'rejected',
  'applied',
  'cancelled',
] as const;
export type PayAdjustmentStatus = (typeof PAY_ADJUSTMENT_STATUSES)[number];
export const payAdjustmentStatusSchema = z.enum(PAY_ADJUSTMENT_STATUSES);

/**
 * `pay_approvals.stage`: `hr_owner` is the Owner approving both stages when no independent
 * `pay_approver` exists (D-098); `payout_owner` is the Owner approving a payout export above the
 * threshold (D-105).
 */
export const PAY_APPROVAL_STAGES = ['hr', 'owner', 'hr_owner', 'payout_owner'] as const;
export type PayApprovalStage = (typeof PAY_APPROVAL_STAGES)[number];
export const payApprovalStageSchema = z.enum(PAY_APPROVAL_STAGES);

/** `pay_approvals.decision`. */
export const PAY_APPROVAL_DECISIONS = ['approved', 'sent_back', 'rejected'] as const;
export type PayApprovalDecision = (typeof PAY_APPROVAL_DECISIONS)[number];
export const payApprovalDecisionSchema = z.enum(PAY_APPROVAL_DECISIONS);

// ---------------------------------------------------------------- payout exports

/** `payout_exports.format`. */
export const PAYOUT_EXPORT_FORMATS = ['csv', 'xlsx', 'hrms'] as const;
export type PayoutExportFormat = (typeof PAYOUT_EXPORT_FORMATS)[number];
export const payoutExportFormatSchema = z.enum(PAYOUT_EXPORT_FORMATS);

/** `payout_exports.status` (the payout export machine, PLAN §3.6). */
export const PAYOUT_EXPORT_STATUSES = [
  'pending_owner_approval',
  'generated',
  'handed_to_payroll',
  'void',
  'rejected',
] as const;
export type PayoutExportStatusValue = (typeof PAYOUT_EXPORT_STATUSES)[number];
export const payoutExportStatusSchema = z.enum(PAYOUT_EXPORT_STATUSES);

// ---------------------------------------------------------------- pay-affecting member details

/**
 * `member_detail_changes.kind` (D-099, D-951): an edit of a person's payroll employee code or date
 * of joining is stored as pending and applied to `workspace_members` only when a second person
 * (an `hr_compensation` holder or the Owner, neither the requester nor the subject) approves it.
 * Both values are also kinds of the pending-input-changes contract (`PAY_INPUT_CHANGE_KINDS`).
 */
export const MEMBER_DETAIL_CHANGE_KINDS = ['employee_code', 'date_of_joining'] as const;
export type MemberDetailChangeKind = (typeof MEMBER_DETAIL_CHANGE_KINDS)[number];
export const memberDetailChangeKindSchema = z.enum(MEMBER_DETAIL_CHANGE_KINDS);

/**
 * `member_detail_changes.status`. One `pending` row per person and kind; a newer request for the
 * same person and kind marks the older one `superseded`. Decided rows never change (trigger).
 */
export const MEMBER_DETAIL_CHANGE_STATUSES = [
  'pending',
  'approved',
  'rejected',
  'superseded',
] as const;
export type MemberDetailChangeStatus = (typeof MEMBER_DETAIL_CHANGE_STATUSES)[number];
export const memberDetailChangeStatusSchema = z.enum(MEMBER_DETAIL_CHANGE_STATUSES);

// ---------------------------------------------------------------- pay audit

/**
 * Pay audit actions (`pay_audit_events.action`). The column only requires the dotted lower-case
 * shape (`<entity>.<verb>`), so a stream may add one without a migration; this list is what the
 * wave-4 services write.
 */
export const PAY_AUDIT_ACTIONS = [
  'pay.compensation.recorded',
  'pay.compensation.closed',
  'pay.plan.created',
  'pay.plan.retired',
  'pay.plan_version.created',
  'pay.plan_version.published',
  'pay.assignment.created',
  'pay.assignment.ended',
  'pay.acknowledged',
  'pay.acknowledgement.recorded_offline',
  'pay.target_statement.acknowledged',
  'pay.period.created',
  'pay.period.transitioned',
  'pay.computation.computed',
  'pay.computation.recomputed',
  'pay.dispute.raised',
  'pay.dispute.resolved',
  'pay.gate_override.proposed',
  'pay.gate_override.approved',
  'pay.adjustment.proposed',
  'pay.adjustment.approved',
  'pay.adjustment.rejected',
  'pay.adjustment.cancelled',
  'pay.approval.recorded',
  'pay.export.prepared',
  'pay.export.generated',
  'pay.export.downloaded',
  'pay.export.voided',
  'pay.setting.changed',
  'pay.grant.changed',
  'pay.hierarchy.changed',
  // A person's payroll employee code or date of joining (D-099, D-957): requested and applied only
  // after a second person approves, or withdrawn, rejected or replaced by a newer request.
  'pay.member.employment_change_requested',
  'pay.member.employment_changed',
  'pay.member.employment_change_rejected',
  'pay.member.employment_change_superseded',
  // A leave day, a holiday, a seat assignment or a KPI mapping changed outside the pay screens (D-958).
  'pay.input.changed',
  'pay.key.rewrapped',
  'pay.key.resealed',
  'pay.audit_chain_anchored',
  'kpi.override.approved',
  'kpi.verification.force_cleared',
  'kpi.entry.changed_in_dispute',
] as const;
export type PayAuditAction = (typeof PAY_AUDIT_ACTIONS)[number];
